import { api } from '../api.js';
import { el } from '../dom.js';
import { t } from '../i18n.js';

// Which institution a `/panel/i/<nickname>/…` page is about.
//
// The nickname rather than an id, because this address is one somebody reads,
// bookmarks and pastes to a colleague — and it is the same nickname the public
// page carries, so `/i/tresor` and `/panel/i/tresor` are visibly the same
// institution seen from two sides.
//
// Resolved against what this person actually maintains rather than trusted:
// typing a nickname is not access, and the API would refuse anyway — this
// turns that refusal into a sentence instead of a stack trace.

export class NoAccessError extends Error {}

export async function institutionScope(nickname) {
  const accounts = await api.get('/accounts');
  const account = accounts.find((row) => !row.isSelf && row.slug === nickname);
  if (!account) throw new NoAccessError();
  return { accounts, account };
}

export function accessDenied() {
  return el('div', { class: 'pub-empty' }, [
    el('p', {}, t.notFound),
    el('p', { class: 'card-meta' }, [
      el('a', { href: '/panel' }, t.backToPanel),
    ]),
  ]);
}

// The institution's mark, one shape everywhere in the panel: square, because
// round is a person, with the monogram standing in until a picture exists —
// the same convention the public pages render.
export function institutionAvatar(account) {
  const classes = 'avatar avatar-xl avatar-square';
  if (account.imageUrl) {
    return el('img', { class: classes, src: account.imageUrl, alt: '', 'aria-hidden': 'true', loading: 'lazy' });
  }
  return el('span', { class: `${classes} avatar-mono`, 'aria-hidden': 'true' },
    (account.name ?? '?').trim().slice(0, 1).toUpperCase());
}

// The heading every institution page carries: whose workspace this is, where
// its public page lives, and the three things there are to do with it.
//
// A local nav rather than the site header's: the site header names places that
// belong to *you*, and these belong to the institution. Putting them in the
// same row would say a person has a calendar page, which they do not — they
// have their own account page instead.
export function institutionHead(scope, current) {
  const base = `/panel/i/${scope.account.slug}`;
  const entries = [
    ['dates', '', t.navDates],
    ['page', '/seite', t.navPage],
    ['account', '/konto', t.navAccountSettings],
  ];

  return el('header', { class: 'account-bar' }, [
    el('div', { class: 'row' }, [
      institutionAvatar(scope.account),
      el('div', {}, [
        el('p', { class: 'card-meta' }, [
          el('a', { href: '/panel' }, t.backToPanel),
        ]),
        el('h1', { class: 'pub-title' }, scope.account.name),
        el('p', { class: 'pub-lede' }, [
          el('a', { href: `/${scope.account.slug}`, target: '_blank', rel: 'noopener' },
            `tba.tips/${scope.account.slug}`),
          scope.account.cityName ? ` · ${scope.account.cityName}` : '',
        ]),
      ]),
    ]),
    el('nav', { class: 'row row-tight', 'aria-label': scope.account.name },
      entries.map(([key, suffix, label]) => el('a', {
        class: `button button-quiet${key === current ? ' is-current' : ''}`,
        href: `${base}${suffix}`,
        'aria-current': key === current ? 'page' : null,
      }, label))),
  ]);
}
